/**
 * QuickReplies.js — Settings sub-screen for managing canned replies.
 *
 * #170 — two-tier model:
 *   - SHARED  (scope='account')  → admin-managed, visible to every agent.
 *                                  Owner sees full edit; agents see read-only.
 *   - PERSONAL (scope='agent')   → each user manages their own. Owner +
 *                                  agents alike. Hidden if the owner has
 *                                  toggled "allow agents to create personal".
 *
 * Owner additionally sees a settings row at the bottom to flip the
 * per-account "allow agents to create personal" toggle.
 *
 * Layout:
 *   ─────── SHARED · set by admin ───────
 *   /hi    Hi! How can I help?       [🔒]   ← agent: lock; owner: edit/del
 *   /faq   See FAQs at link.com      [🔒]
 *   [+ New shared reply]   ← owner only
 *
 *   ─────── MY REPLIES ───────
 *   /chk   Just a sec, checking      ✎ 🗑
 *   [+ New personal reply]
 *
 *   Owner settings:
 *   ☑ Allow agents to create personal replies
 *
 * Slash expansion shows BOTH /hi entries (shared + personal) — the agent
 * picks which one. The dropdown puts personal first so it's the
 * highlighted default; visual differentiation tells them apart.
 */

import { html }              from 'htm/preact';
import { useState, useEffect } from 'preact/hooks';
import { useStore }          from '@nanostores/preact';

import { navigate, showToast } from '../stores/ui.js';
import { SCREENS }           from '../shared/constants.js';
import { $session, isOwner } from '../stores/session.js';
import {
  $quickReplies,
  addQuickReply,
  updateQuickReply,
  removeQuickReply,
  normalizeShortcut,
} from '../services/quick-replies.js';
import * as api from '../services/api.js';

export function QuickReplies() {
  const replies = useStore($quickReplies);
  const session = useStore($session);
  const owner   = isOwner();

  // null               → form hidden
  // { scope, shortcut, text, id? } → form visible (id present = edit)
  const [draft, setDraft]   = useState(null);
  const [saving, setSaving] = useState(false);

  // Per-account toggle (owner-managed). Loaded async; defaults to true
  // until we hear otherwise so the UI doesn't blink.
  const [allowAgentReplies,    setAllowAgentReplies]    = useState(true);
  const [savingToggle,         setSavingToggle]         = useState(false);

  useEffect(() => {
    api.fetchAccountSettings()
      .then(res => {
        const v = res?.data?.allow_agent_quick_replies;
        if (typeof v === 'boolean') setAllowAgentReplies(v);
      })
      .catch(() => { /* default true stays */ });
  }, []);

  const toggleAllowAgentReplies = async () => {
    if (savingToggle) return;
    const next = !allowAgentReplies;
    setAllowAgentReplies(next);  // optimistic
    setSavingToggle(true);
    try {
      await api.updateAccountSettings({ allow_agent_quick_replies: next });
      showToast(next
        ? 'Agents can create personal replies — existing ones restored'
        : 'Agent personal replies hidden — restored if you turn this back on'
      , 'success');
    } catch (e) {
      setAllowAgentReplies(!next);  // rollback
      showToast(e?.message || 'Could not update', 'error');
    } finally {
      setSavingToggle(false);
    }
  };

  // ── Draft helpers ────────────────────────────────────────────────────────
  const openNew  = (scope) => setDraft({ scope, shortcut: '', text: '' });
  const openEdit = (r)     => setDraft({ id: r.id, scope: r.scope || 'account', shortcut: r.shortcut, text: r.text });
  const cancel   = ()      => setDraft(null);

  const handleSave = async () => {
    if (!draft || saving) return;
    setSaving(true);
    try {
      if (draft.id) {
        await updateQuickReply(draft.id, { shortcut: draft.shortcut, text: draft.text });
      } else {
        await addQuickReply({ shortcut: draft.shortcut, text: draft.text, scope: draft.scope });
      }
      setDraft(null);
      showToast('Saved', 'success');
    } catch (e) {
      showToast(e.message || 'Could not save', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (r) => {
    try {
      await removeQuickReply(r.id);
      showToast(`Removed /${r.shortcut}`, 'success');
    } catch (e) {
      showToast(`Could not delete: ${e.message}`, 'error');
    }
  };

  // ── Bucket the list ──────────────────────────────────────────────────────
  const sharedReplies   = replies.filter(r => (r.scope || 'account') === 'account');
  const personalReplies = replies.filter(r => (r.scope || 'account') === 'agent');

  // Agents see the personal section only when the toggle is on. When the
  // owner flips it off, the server stops returning the agent's own personal
  // rows on the next hydrate (they're hidden, not deleted — flipping the
  // toggle back on restores everything). So when toggle is off + non-owner,
  // we hide the entire section to avoid an awkward empty state.
  const personalSectionVisible = owner || allowAgentReplies;

  // ── Render ───────────────────────────────────────────────────────────────
  return html`
    <div class="inbox-screen" style="display:flex">
      <!-- Header -->
      <div class="inbox-header">
        <button class="inbox-header-icon-btn" onClick=${() => navigate(SCREENS.SETTINGS)} title="Back">
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="m15 18-6-6 6-6"/>
          </svg>
        </button>
        <h2 class="inbox-header-title">Quick Replies</h2>
      </div>

      <!-- Content (scrollable) -->
      <div style="flex:1;overflow-y:auto;padding:14px">

        <!-- Empty-state explainer (only when both buckets are empty AND no draft) -->
        ${!draft && replies.length === 0 && html`
          <div class="inbox-qr-explainer">
            Save canned replies you send often. Type
            <code>/shortcut</code> + space in the composer to expand.
            ${owner ? ' Shared replies are visible to every agent. Personal replies are private.' : ' Shared replies are set up by your account admin. Personal replies are yours alone.'}
          </div>
        `}

        <!-- Add/Edit form (rendered above whichever section it belongs to) -->
        ${draft && html`
          <${DraftForm}
            draft=${draft}
            saving=${saving}
            onChange=${setDraft}
            onCancel=${cancel}
            onSave=${handleSave}
          />
        `}

        <!-- ── SHARED section (scope=account) ────────────────────────── -->
        <section class="inbox-qr-section">
          <div class="inbox-qr-section-head">
            <span class="inbox-qr-section-label">
              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" style="vertical-align:-1px;margin-right:4px"><path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z"/></svg>
              Shared · set by admin
            </span>
            <span class="inbox-qr-section-count">${sharedReplies.length}</span>
          </div>

          ${sharedReplies.length === 0 && html`
            <div class="inbox-qr-empty">
              ${owner
                ? 'No shared replies yet. Add one — every agent will see it.'
                : 'Your admin hasn\'t added any shared replies yet.'}
            </div>
          `}
          ${sharedReplies.map(r => html`
            <${ReplyRow}
              key=${r.id}
              reply=${r}
              canEdit=${owner && !r._pending}
              onEdit=${() => openEdit(r)}
              onDelete=${() => handleDelete(r)}
            />
          `)}

          ${owner && !draft && html`
            <button class="inbox-qr-add-btn" onClick=${() => openNew('account')}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
              New shared reply
            </button>
          `}
        </section>

        <!-- ── PERSONAL section (scope=agent) ──────────────────────── -->
        ${personalSectionVisible && html`
          <section class="inbox-qr-section">
            <div class="inbox-qr-section-head">
              <span class="inbox-qr-section-label">
                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" style="vertical-align:-1px;margin-right:4px"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
                ${owner ? 'My personal replies' : 'My replies'}
              </span>
              <span class="inbox-qr-section-count">${personalReplies.length}</span>
            </div>

            ${personalReplies.length === 0 && html`
              <div class="inbox-qr-empty">
                Nothing yet. Add a reply only you can see and edit.
              </div>
            `}
            ${personalReplies.map(r => html`
              <${ReplyRow}
                key=${r.id}
                reply=${r}
                canEdit=${!r._pending}
                onEdit=${() => openEdit(r)}
                onDelete=${() => handleDelete(r)}
              />
            `)}

            ${(owner || allowAgentReplies) && !draft && html`
              <button class="inbox-qr-add-btn" onClick=${() => openNew('agent')}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                New personal reply
              </button>
            `}
          </section>
        `}

        <!-- Owner-only account toggle -->
        ${owner && html`
          <section class="inbox-qr-owner-settings">
            <div class="inbox-qr-section-label" style="margin-bottom:8px">Owner settings</div>
            <label class="inbox-qr-toggle-row">
              <span style="flex:1">
                <div style="font-size:0.85rem;font-weight:600;color:var(--v2-text)">Allow agents to create personal replies</div>
                <div style="font-size:0.75rem;color:var(--text-muted, #a4adc1);margin-top:2px">
                  When off, agents only see the shared replies above. Their existing personal replies are hidden — turn this back on to restore them.
                </div>
              </span>
              <span class=${`inbox-qr-toggle ${allowAgentReplies ? 'is-on' : ''}`} aria-hidden="true">
                <span class="inbox-qr-toggle-knob"></span>
              </span>
              <input
                type="checkbox"
                checked=${allowAgentReplies}
                onChange=${toggleAllowAgentReplies}
                disabled=${savingToggle}
                style="position:absolute;opacity:0;width:0;height:0"
                aria-label="Allow agents to create personal replies"
              />
            </label>
          </section>
        `}

      </div>
    </div>
  `;
}

// ── Sub-components ──────────────────────────────────────────────────────────

function DraftForm({ draft, saving, onChange, onCancel, onSave }) {
  const isShared = draft.scope === 'account';
  return html`
    <div class=${`inbox-qr-form ${isShared ? 'is-shared' : 'is-personal'}`}>
      <div class="inbox-qr-form-head">
        ${draft.id ? 'Edit' : 'New'} ${isShared ? 'shared reply' : 'personal reply'}
      </div>

      <label style="display:block;font-size:0.7rem;color:var(--text-muted, #7d8799);margin-bottom:4px">Shortcut</label>
      <div style="display:flex;align-items:center;gap:0;margin-bottom:12px">
        <span style="background:rgba(255,255,255,0.08);border:1px solid rgba(255,255,255,0.12);border-right:none;border-radius:8px 0 0 8px;padding:9px 10px;color:var(--text-muted, #7d8799);font-weight:700;font-family:ui-monospace,Menlo,monospace;font-size:0.85rem">/</span>
        <input
          type="text"
          value=${draft.shortcut}
          onInput=${(e) => onChange({ ...draft, shortcut: normalizeShortcut(e.target.value) })}
          placeholder="greet"
          autofocus
          style="flex:1;background:rgba(0,0,0,0.3);border:1px solid rgba(255,255,255,0.12);border-left:none;border-radius:0 8px 8px 0;padding:9px 11px;color:inherit;font-size:0.85rem;font-family:ui-monospace,Menlo,monospace"
        />
      </div>

      <label style="display:block;font-size:0.7rem;color:var(--text-muted, #7d8799);margin-bottom:4px">Reply text</label>
      <textarea
        value=${draft.text}
        onInput=${(e) => onChange({ ...draft, text: e.target.value })}
        placeholder="Hi! Thanks for reaching out — how can I help?"
        rows="4"
        style="width:100%;background:rgba(0,0,0,0.3);border:1px solid rgba(255,255,255,0.12);border-radius:8px;padding:10px 12px;color:inherit;font-size:0.88rem;font-family:inherit;resize:vertical;line-height:1.45"
      ></textarea>

      <div style="display:flex;justify-content:flex-end;gap:8px;margin-top:12px">
        <button onClick=${onCancel} style="background:rgba(255,255,255,0.08);color:var(--text-secondary, #a4adc1);border:none;border-radius:8px;padding:8px 14px;font-size:0.8rem;font-weight:600;cursor:pointer">Cancel</button>
        <button onClick=${onSave} disabled=${saving} style="background:#25D366;color:#0a0c12;border:none;border-radius:8px;padding:8px 16px;font-size:0.8rem;font-weight:700;cursor:${saving ? 'wait' : 'pointer'};opacity:${saving ? 0.7 : 1}">${saving ? 'Saving…' : (draft.id ? 'Update' : 'Save')}</button>
      </div>
    </div>
  `;
}

function ReplyRow({ reply, canEdit, onEdit, onDelete }) {
  const isShared = (reply.scope || 'account') === 'account';
  const cls = `inbox-qr-row ${isShared ? 'is-shared' : 'is-personal'}${reply._pending ? ' is-pending' : ''}`;
  return html`
    <div class=${cls} data-qr-id=${reply.id} data-qr-shortcut=${reply.shortcut}>
      <div style="display:flex;align-items:center;gap:8px;margin-bottom:6px">
        <span class=${`inbox-qr-shortcut ${isShared ? 'is-shared' : 'is-personal'}`}>/${reply.shortcut}</span>
        ${isShared && html`
          <span class="inbox-qr-shared-badge" title="Shared reply set by your account admin">
            <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.6" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
            shared
          </span>
        `}
        <div style="flex:1"></div>
        ${reply._pending && html`
          <span style="display:inline-flex;align-items:center;gap:5px;font-size:0.68rem;color:var(--text-muted, #7d8799);font-style:italic">
            <span class="inbox-spinner" style="width:10px;height:10px;border-width:1.5px"></span>
            Saving…
          </span>
        `}
        ${canEdit && html`
          <button onClick=${onEdit} title="Edit" style="background:transparent;border:none;color:var(--text-muted, #7d8799);cursor:pointer;padding:4px">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
          </button>
          <button onClick=${onDelete} title="Delete" style="background:transparent;border:none;color:#ef4444;cursor:pointer;padding:4px">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>
          </button>
        `}
      </div>
      <div style="font-size:0.82rem;line-height:1.45;color:var(--v2-text);white-space:pre-wrap;word-break:break-word">${reply.text}</div>
    </div>
  `;
}
